import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Grid} from '@material-ui/core';
import {useFormik} from 'formik';
import {useTranslation} from 'react-i18next';

import {useDispatch, useSelector} from 'react-redux';
import dayjs from '../../services/customDayJs';
import {ReduxState} from '../../store/types';
import classNames from 'classnames';
import {
    dateFormat,
    dateFromDateTillValidationSchema,
    generateColumns,
    initialVoicemailDetailsDialogProps,
    saveObjectUrl,
    useStyles,
    VoicemailsDialogProps,
    VoicemailsFormProps,
} from './VoicemailsDialog.utils';
import DialogContainer, {DialogButton} from '../../components/AlertDialog/DialogContainer';
import {Voicemail} from '../../store/actions/payloads';
import CustomKeyboardDateTimePicker from '../../components/KeyboardDateTimePicker/KeyboardDateTimePicker';
import Button from '../../components/Button/Button';
import DataGrid from '../../components/DataGrid/DataGrid';
import {PaginationMode} from '../../components/DataGrid/types';
import {actions} from '../../store';
import AlertDialog from '../../components/AlertDialog/AlertDialog';
import {RecordingsPlayingState, RecordingsPlayingStatus} from '../../store/reducers/calls/reducer';
import {VoicemailDetailsDialogProps} from './VoicemailDetailsDialog.utils';
import VoicemailDetailsDialog from './VoicemailDetailsDialog';
import { convertUserLocalTimeToUtc } from '../../utils/dateWithTimezoneConversion';

const VoicemailsDialog: React.VFC<VoicemailsDialogProps> = (
    {
        isOpen,
        toggleVisibility,
        extensionId,
        extensionName,
        i_account,
        fetchData
    }) => {
    const classes = useStyles();
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const initPageSize = 10;
    const [searchPageSize, setSearchPageSize] = useState<number>(initPageSize);
    const [dialogVisible, setDialogVisible] = useState(false);
    const [messageToDelete, setMessageToDelete] = useState<number>(0);

    const [detailsDialogProps, setDetailsDialogProps] = useState<VoicemailDetailsDialogProps>(initialVoicemailDetailsDialogProps);

    useEffect(() => {
        if(!isOpen) {
            setDetailsDialogProps(initialVoicemailDetailsDialogProps);
        }
    }, [isOpen]);

    const [voicemails, setVoicemails] = useState<Voicemail[]>([]);

    const timezoneOffset = useSelector<ReduxState, number>(
        (state) => state.generic.sessionData?.tz_offset || 0,
    );

    const userDateTimeFormat = useSelector<ReduxState, string>(
        (state) =>
            state.generic.globalCustomerInfo?.customer_info
                ?.out_date_time_format || '',
    );
    
    const {accountVoicemails, isUpdatingMessageFlag, isDeletingVoicemail} = useSelector((state: ReduxState) => state.voicemails);
    const recordingsPlayer: RecordingsPlayingState = useSelector<ReduxState, RecordingsPlayingState>(
        (state) => state.calls.recordingsPlayer,
    );

    const isLoadingVoicemails = useMemo(() => {
        return accountVoicemails?.find(e => e.isLoadingVoicemails) !== undefined;
    }, [accountVoicemails]);
    
    const initialValues = useMemo(
        () => ({
            from: dayjs
                .utc()
                .utcOffset(timezoneOffset / 60)
                .subtract(1, 'month')
                .format(dateFormat),
            till: dayjs
                .utc()
                .utcOffset(timezoneOffset / 60)
                .format(dateFormat),
            cli: '',
            cld: '',
        }),
        [],
    );

    const customDownloadFileAction = useCallback((message_uid: number, 
            callback: (url: string) => void, 
            restrictedCallback?: () => void) =>
    {
        dispatch(
            actions.getVoicemailFile.request({
                format: "wav",
                i_account: i_account,
                message_uid: message_uid,
                callback: callback,
                restrictedCallback: restrictedCallback
            }),
        );
    }, [i_account]);

    const getVoicemails = useCallback((
        formData: VoicemailsFormProps,
        limit?: number,
        offset?: number,
    ) => {
        const backendTimeFormat = "DD-MMM-YYYY HH:mm:ss Z";
        let filtered = accountVoicemails
            ?.filter(e => e.i_account == i_account)
            ?.[0]?.voicemails
            ?? [];
        filtered.sort((a: Voicemail, b: Voicemail) => dayjs(b.delivery_date, backendTimeFormat)
            .isAfter(dayjs(a.delivery_date, backendTimeFormat)) ? 1 : -1);
        if(formData?.from) {
            const fromdjs = dayjs.utc(convertUserLocalTimeToUtc(formData.from, timezoneOffset)
                , userDateTimeFormat);
            filtered = filtered.filter(e => e.delivery_date
                && dayjs(e.delivery_date, backendTimeFormat).isAfter(fromdjs));
        }
        if(formData?.till) {
            const tilljs = dayjs.utc(convertUserLocalTimeToUtc(formData.till, timezoneOffset)
                , userDateTimeFormat);
            filtered = filtered.filter(e => e.delivery_date
                && tilljs.isAfter(dayjs(e.delivery_date, backendTimeFormat)));
        }
        const toStore = filtered
            .slice(offset, (offset ?? 0) + (limit ?? initPageSize));
        setVoicemails(toStore);
    }, [i_account, accountVoicemails, setVoicemails, userDateTimeFormat, timezoneOffset]);

    const {
        values,
        handleSubmit,
        setFieldValue,
        resetForm,
        errors,
        setFieldError,
    } = useFormik<VoicemailsFormProps>({
        initialValues,
        onSubmit: (form) => {
            getVoicemails(form, searchPageSize, 0);
        },
        validationSchema: dateFromDateTillValidationSchema(timezoneOffset, userDateTimeFormat),
        enableReinitialize: true,
        validateOnChange: false,
    });

    const voicemailsNoPaging = useMemo(() => {
        return accountVoicemails
            ?.filter(e => e.i_account == i_account)
            ?.[0]?.voicemails
            ?? [];
    }, [accountVoicemails, i_account]);

    useEffect(() => {
        getVoicemails(values, initPageSize, 0);
    }, [accountVoicemails, i_account]);

    const onCloseDetailsDialog = useCallback(() => {
        setDetailsDialogProps({
            ...detailsDialogProps,
            isOpen: false
        });
        setTimeout(() => {
            setDetailsDialogProps(initialVoicemailDetailsDialogProps);
        }, 500);

    }, [detailsDialogProps, initialVoicemailDetailsDialogProps]);

    const onClickDelete = useCallback(() => {
        dispatch(
            actions.deleteVoicemail.request({
                message_uids: [messageToDelete],
                i_account: i_account,
                callback: () => {
                    setDialogVisible(false);
                    onCloseDetailsDialog();
                    fetchData();
                }
            }),
        );
    }, [messageToDelete, setDialogVisible, i_account, fetchData, onCloseDetailsDialog]);
    
    const onDeleteMessage = useCallback((message_id: number) => {
        setMessageToDelete(message_id);
        setDialogVisible(true);
    }, [setMessageToDelete, setDialogVisible]);
    
    const onClickStarred = useCallback((message_id: number, newValue: boolean) => {
        dispatch(actions.setMailboxMessagesFlag.request({
            action: newValue ? "set_flag" : "remove_flag",
            flag: 'Flagged',
            i_account: i_account,
            message_uids: [message_id]
        }));
    }, [i_account]);

    const onClickDetails = useCallback((voicemail: Voicemail) => {
        setDetailsDialogProps({
            isOpen: true,
            voicemail: voicemail,
            timezoneOffset: timezoneOffset,
            onDeleteClick: () => {
                onDeleteMessage(voicemail.message_uid);
            },
            onDownloadClick: () => {
                customDownloadFileAction(voicemail.message_uid, (url) => {
                    saveObjectUrl(url, "voicemail_" + voicemail.message_uid + ".wav");
                }, undefined);
            },
            extensionId: extensionId,
            extensionName: extensionName,
            toggleVisibility: onCloseDetailsDialog,
            i_account: i_account,
            onClickStarred: onClickStarred,
            customDownloadFileAction: customDownloadFileAction
        });
    }, [extensionId, extensionName, timezoneOffset, onCloseDetailsDialog]);

    const columns = generateColumns(
        t,
        classes,
        timezoneOffset,
        userDateTimeFormat,
        customDownloadFileAction,
        onDeleteMessage,
        onClickStarred,
        onClickDetails
    );

    const data = useMemo(() => voicemails?.map((v) => ({id: v.message_uid, ...v})), [voicemails]);

    return (
        <DialogContainer
            isOpen={isOpen}
            dataQa="voicemails-dialog"
            header={t('screens:voicemails.voicemailsDialogTitle', {
                extensionNumber: extensionId,
                name: extensionName ?? t('screens:autoAttendants.unnamed'),
                })
            }
            headerClass={classes.header}
            className={classes.modalContainer}
            dialogActionsButtons={[
                <DialogButton
                    className={classes.primaryActionButton}
                    key="close"
                    label={t('common:close')}
                    onClick={() => {
                        resetForm();
                        toggleVisibility?.();
                    }}
                />,
            ]}
            showLoader={isLoadingVoicemails}
        >
            <form
                onSubmit={handleSubmit}
                autoComplete="off"
                data-testid="voicemails-form"
            >
                <Grid
                    item
                    className={classNames(
                        classes.itemsContainer,
                        classes.filterSeparation,
                    )}
                >
                    <CustomKeyboardDateTimePicker
                        id="from"
                        label={t('common:fromDate')}
                        value={values.from}
                        onChange={(v) => setFieldValue('from', v)}
                        dataQa={'voicemails-filter-from'}
                        dataTestId={'voicemails-filter-from'}
                        userDateTimeFormat={userDateTimeFormat}
                        handleSubmit={handleSubmit}
                        helperText={errors.from}
                        setFieldError={setFieldError}
                        className={classes.timePicker}
                        skipPermission
                    />
                    <CustomKeyboardDateTimePicker
                        id="till"
                        label={t('common:toDate')}
                        value={values.till}
                        onChange={(v) => setFieldValue('till', v)}
                        dataQa={'voicemails-filter-till'}
                        dataTestId={'voicemails-filter-till'}
                        userDateTimeFormat={userDateTimeFormat}
                        handleSubmit={handleSubmit}
                        helperText={errors.till}
                        setFieldError={setFieldError}
                        className={classes.timePicker}
                        skipPermission
                    />
                    <Button
                        primary
                        accent
                        dataQa="voicemails-search"
                        className={classes.button}
                        onClick={() => handleSubmit()}
                        skipPermission
                    >
                        {t('common:search')}
                    </Button>
                </Grid>
            </form>

            <DataGrid
                className={classes.table}
                classes={{pagination: classes.pagination}}
                columns={columns}
                data={data}
                rowCount={voicemailsNoPaging?.length || 0}
                loading={isUpdatingMessageFlag}
                onPageChange={(v) =>
                    getVoicemails(
                        values,
                        v.pageSize,
                        v.pageSize * v.page,
                    )
                }
                onPageSizeChange={(v) => {
                    setSearchPageSize(v.pageSize)
                    getVoicemails(values, v.pageSize, 0)
                }
                }
                paginationMode={PaginationMode.Client}
                centeredRows
                pageSizeOptions={[5, 10, 15, 25, 50]}
                initialPageSize={initPageSize}
                narrowRows
                customRowStyle={(row) => {
                    const messageId = row.original.message_uid;
                    if(messageId && (
                        recordingsPlayer.status === RecordingsPlayingStatus.playing ||
                        recordingsPlayer.status === RecordingsPlayingStatus.paused)) 
                    {
                        if(!(recordingsPlayer?.currentFile)) return '';
                        if(messageId === recordingsPlayer.currentFile.i_xdr)
                        {
                            return classes.playingMusicRow;
                        }
                    }
                    return '';
                }}
            />

            <AlertDialog
                isOpen={dialogVisible}
                description={t('screens:voicemails.deleteSelectedVoicemail')}
                contentClass="alert-content"
                className={classes.confirmDialogContainer}
                hideHeader={true}
                dialogActionsButtons={[
                    <DialogButton
                        key="cancel"
                        label={t('common:cancel')}
                        onClick={() => setDialogVisible(false)}
                        hidden={isDeletingVoicemail || !dialogVisible}
                    />,
                    <DialogButton
                        key="delete"
                        label={t('common:delete')}
                        onClick={onClickDelete}
                        dataQa="confirm-delete"
                        loadingAnimation={isDeletingVoicemail || !dialogVisible}
                    />,
                ]}
                dataQa="remove-modal"
                dataTestId="remove-modal"
            />

            <VoicemailDetailsDialog {...detailsDialogProps} />
        </DialogContainer>
    );
};

export default VoicemailsDialog;
